// The stages this backoffice can talk to.
//
// One deployment, hosted on cons, reaches every stage: the Cognito pool is shared, so the
// same access token is accepted by each stage's FES. Only the FES host changes, which is why
// this table carries nothing else — each stage keeps its own database, backend API key and
// GitHub App, and none of that is addressable from here.
//
// Shaped after agent-cli/src/profiles.ts, which already solved this for the CLI.

const lastStageKey = 'agent_backoffice_active_stage';

export type StageName = 'dev' | 'int' | 'cons' | 'prod';

export type Stage = {
  name: StageName | 'local';
  label: string;
  fesBaseUrl: string;
  isProduction: boolean;
};

const deployedStages: Stage[] = [
  { name: 'dev', label: 'DEV', fesBaseUrl: 'https://api.dev.ai-agent.vwfs.io', isProduction: false },
  { name: 'int', label: 'INT', fesBaseUrl: 'https://api.int.ai-agent.vwfs.io', isProduction: false },
  { name: 'cons', label: 'CONS', fesBaseUrl: 'https://api.cons.ai-agent.vwfs.io', isProduction: false },
  { name: 'prod', label: 'PROD', fesBaseUrl: 'https://api.ai-agent.vwfs.io', isProduction: true },
];

const fallbackStageName: StageName = 'cons';

/**
 * Assigning `undefined` to a `process.env` entry stores the literal string "undefined"
 * rather than clearing it, and dotenv.js writes whatever an env file holds, so both of those
 * read back as a stage name that matches nothing. Treat them as unset.
 */
function readEnv(name: string): string {
  // Create React App replaces `process.env` with an object literal holding every REACT_APP_*
  // variable present at build time, so indexing it dynamically is safe here. A variable not
  // set when the bundle was built is simply absent.
  const value = (process.env[name] || '').trim();
  return value === 'undefined' || value === 'null' ? '' : value;
}

function buildStageName(): string {
  return readEnv('REACT_APP_STAGE').toLowerCase();
}

/**
 * `yarn dev` runs against src/setupProxy.js, which stands in for FES on the dev server's own
 * origin. There is no stage to choose in that mode — the proxy's target is whatever
 * FES_PROXY_TARGET names — so the switcher collapses to a single entry.
 */
export function isLocalProxyBuild(): boolean {
  return buildStageName() === 'local';
}

function localStage(): Stage {
  return {
    name: 'local',
    label: 'LOCAL PROXY',
    fesBaseUrl: readEnv('REACT_APP_FES_BASE_URL'),
    isProduction: false,
  };
}

/**
 * Resolves each stage's host, most specific first:
 *
 *   1. REACT_APP_FES_BASE_URL_<STAGE> — names one stage outright. Needed to point a stage at
 *      a FES running locally, and useful for a feature-test deployment.
 *   2. REACT_APP_FES_BASE_URL — the stage this build was made for, so the env files keep
 *      deciding their own host.
 *   3. The table above.
 */
function withHostOverrides(stages: Stage[]): Stage[] {
  const buildOverride = readEnv('REACT_APP_FES_BASE_URL');
  // Without REACT_APP_STAGE the build override still belongs to whichever stage this opens
  // on, which is the fallback.
  const buildStage = buildStageName() || fallbackStageName;

  return stages.map(stage => {
    const stageOverride = readEnv(`REACT_APP_FES_BASE_URL_${stage.name.toUpperCase()}`);

    if (stageOverride) {
      return { ...stage, fesBaseUrl: stageOverride };
    }

    if (buildOverride && stage.name === buildStage) {
      return { ...stage, fesBaseUrl: buildOverride };
    }

    return stage;
  });
}

export function allStages(): Stage[] {
  return isLocalProxyBuild() ? [localStage()] : withHostOverrides(deployedStages);
}

export function groupsForStage(stage: Stage): { users: string; admins: string } {
  return { users: `aca-${stage.name}-users`, admins: `aca-${stage.name}-admins` };
}

/**
 * The stages this token is entitled to. FES refuses a caller holding no group for the stage
 * it serves, so offering one here that the token cannot reach would only produce a 403.
 */
export function entitledStages(groups: string[]): Stage[] {
  if (isLocalProxyBuild()) {
    return allStages();
  }

  const held = new Set(groups);

  return allStages().filter(stage => {
    const { users, admins } = groupsForStage(stage);
    return held.has(users) || held.has(admins);
  });
}

function findStage(stages: Stage[], name: string | null | undefined): Stage | undefined {
  return name ? stages.find(stage => stage.name === name) : undefined;
}

export function readLastStage(): string | null {
  try {
    return window.localStorage.getItem(lastStageKey);
  } catch {
    return null;
  }
}

export function writeLastStage(stage: Stage): void {
  try {
    window.localStorage.setItem(lastStageKey, stage.name);
  } catch {
    // A blocked or full store only costs the convenience of reopening on the same stage.
  }
}

/**
 * Last stage used, then the one this build was made for, then whatever the token can reach.
 * Falls back to the build's own stage when nothing is entitled, so a user with no groups sees
 * FES's 403 rather than an empty switcher and no explanation.
 */
export function pickInitialStage(entitled: Stage[]): Stage {
  const stages = allStages();
  const buildStage = findStage(stages, buildStageName()) ?? findStage(stages, fallbackStageName) ?? stages[0];

  return (
    findStage(entitled, readLastStage())
    ?? findStage(entitled, buildStage.name)
    ?? entitled[0]
    ?? buildStage
  );
}
